<script lang="ts">
    import type {
        DocumentDetailReactivePageData,
        DocumentDetailStaticPageData
    } from '../../../record/kp-document-detail-page/types';
    import type {User} from 'src/typings';
    import type {RelatedRecordsTable} from '../../../record/kp-related-records-table/types';
    import type {UserDetailView} from 'src/features/user/types';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {TabGroup} from 'src/features/erp/components/erp-tab-pages/types';
    import {getPageContext} from 'shared/layouts';
    import {getInjector} from 'src/core';
    import {onMount} from 'svelte';
    import {
        modifyTabGroupsForProjectRecord,
        ProjectTopbar
    } from 'src/features/erp-sutor/features/project-record-detail';
    import {modifyTabGroupsForUserRecord} from 'src/features/erp-sutor/features/employee-record-detail';
    import {ErpPageLayout, ErpTabPages, TabPagesRouter} from 'src/features/erp/components';
    import {FOND_PERSON, FOND_PROJECT} from 'src/features/erp-sutor/sutor-fonds';
    import {MediaViewerInline} from 'src/features/media-viewer';
    import {ErpFondPermissionsContextService} from 'src/features/erp';
    import UserDataService from 'src/features/user/user.data-service';
    import GenericRecordDetailTopbar from './components/GenericRecordDetailTopbar.svelte';
    import GenericRecordDetailExportTab from './tab-pages/export/GenericRecordDetailExportTab.svelte';
    import GenericRecordDetailThreadTab from './tab-pages/GenericRecordDetailThreadTab.svelte';
    import GenericRecordDetailDetailTab from './tab-pages/GenericRecordDetailDetailTab.svelte';
    import GenericRecordDetailDebugTab from './tab-pages/GenericRecordDetailDebugTab.svelte';
    import {
        createTabGroupFromRelatedTables,
        RECORD_DETAIL_DETAILS_TAB,
        RECORD_DETAIL_EXPORT_TAB,
        RECORD_DETAIL_FILES_TAB,
        RECORD_DETAIL_THREAD_TAB, RECORD_DETAIL_TOOLS_TAB
    } from './constants';

    export let relatedTables: RelatedRecordsTable[] = [];

    const userDataService = getInjector().getByClass(UserDataService);
    const fondPermissionsService = getInjector().getByClass(ErpFondPermissionsContextService);

    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();
    const fondPermissions = fondPermissionsService.getFondPermissionsValue();
    const model = pageContext.staticData.model;
    const record = model.record;
    const isProjectRecord = FOND_PROJECT.subfonds.includes(record.fond.id);
    const isUserRecord = FOND_PERSON.fond === record.fond.id;

    let loading = true;
    let loadedUser: User | null = null;
    let loadedUserDetail: UserDetailView | null = null;

    onMount(async () => {
        if (!isUserRecord) {
            loading = false;
            return;
        }

        try {
            loadedUser = await userDataService.getUser(record.id);
            loadedUserDetail = await userDataService.getUserDetailView(record.id);
        } catch {
            loadedUser = null;
            loadedUserDetail = null;
        }

        loading = false;
    });

    function resolveTopbarComponent(): SvelteComponentConstructor {
        if (isProjectRecord) {
            return ProjectTopbar;
        }

        return GenericRecordDetailTopbar;
    }

    let tabGroups: TabGroup[] = [
        {
            id: RECORD_DETAIL_DETAILS_TAB,
            label: 'Přehledy',
            icon: 'info',
            subtabs: [
                {
                    id: 'tab-info',
                    label: 'Detail',
                    icon: 'info',
                    render: {
                        component: GenericRecordDetailDetailTab,
                        padded: true
                    }
                },
                {
                    id: 'tab-debug',
                    label: 'Debug',
                    icon: 'bug',
                    render: {
                        component: GenericRecordDetailDebugTab,
                        padded: true
                    }
                },
            ]
        }
    ];

    if (relatedTables.length > 0) {
        tabGroups.push(createTabGroupFromRelatedTables(relatedTables, record));
    }

    if (fondPermissionsService.hasEditFondPermissions(fondPermissions, record.fond.id)) {
        tabGroups.push({
            id: RECORD_DETAIL_TOOLS_TAB,
            label: 'Nástroje',
            icon: 'tools',
            subtabs: [
                {
                    id: RECORD_DETAIL_EXPORT_TAB,
                    label: 'Exporty',
                    icon: 'file-export',
                    render: {
                        component: GenericRecordDetailExportTab
                    }
                }
            ]
        });

        if (record.fond.threadable) {
            tabGroups.push({
                id: RECORD_DETAIL_THREAD_TAB,
                label: 'Konverzace',
                icon: 'messages',
                render: {
                    component: GenericRecordDetailThreadTab
                }
            });
        }

        tabGroups.push({
            id: RECORD_DETAIL_FILES_TAB,
            label: 'Soubory',
            icon: 'file',
            render: {
                component: MediaViewerInline,
                props: {record}
            }
        });
    }

    if (isProjectRecord) {
        tabGroups = modifyTabGroupsForProjectRecord(tabGroups, model.showAnalyticsTab, record);
    }

    if (isUserRecord) {
        tabGroups = modifyTabGroupsForUserRecord(tabGroups, record);
    }
</script>

<ErpPageLayout pageClass="sutor-document-detail-tabbed-page" gap="0px" withoutContentPadding {loading}>
    <svelte:component this={resolveTopbarComponent()}
                      user={loadedUser}
                      userDetailView={loadedUserDetail}/>

    <ErpTabPages {tabGroups}
                 parentIdentifier="record-detail-{record.fond.id}"
                 pageClass="document-detail-tab-pages">

        <TabPagesRouter/>
    </ErpTabPages>
</ErpPageLayout>